const CACHE_VERSION = 1;
const DEFAULT_MAX_AGE_MS = 15 * 60 * 1000;

function normalizeForSignature(value) {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeForSignature(item));
  }
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        const normalized = normalizeForSignature(value[key]);
        if (normalized !== undefined) acc[key] = normalized;
        return acc;
      }, {});
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? Number(value.toFixed(6)) : null;
  }
  return value === undefined ? null : value;
}

function stableStringify(value) {
  try {
    return JSON.stringify(normalizeForSignature(value));
  } catch {
    return "";
  }
}

function compactHash(input) {
  const text = String(input || "");
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16);
}

function getSessionStorage() {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage || null;
  } catch {
    return null;
  }
}

function pickClientFacingSpend(raw = {}) {
  return (
    raw.clientFacingSpend ??
    raw.client_facing_spend ??
    raw.clientSpend ??
    raw.client_spend ??
    raw.spendClientFacing ??
    raw.spend_client_facing ??
    raw.clientBilledSpend ??
    raw.client_billed_spend ??
    raw.billedSpend ??
    raw.billed_spend ??
    raw.effectiveSpend ??
    raw.effective_spend ??
    raw.effectiveSpendRaw ??
    raw.spend
  );
}

export function buildAnalyticsScopeKey(parts = {}) {
  return `scope:${compactHash(stableStringify(parts))}`;
}

export function buildAnalyticsMetricSignature(value = {}) {
  return `metrics:${compactHash(stableStringify(value))}`;
}

export function getToplineMetricSignature(topline) {
  const raw = topline?.raw || {};
  return (
    raw.metricSignature ||
    buildAnalyticsMetricSignature({
      spend: pickClientFacingSpend(raw),
      impressions: raw.impressions,
      clicks: raw.clicks,
      conversions: raw.conversions,
      engagements: raw.engagements,
      videoViews: raw.videoViews,
      videoStarts: raw.videoStarts,
      videoCompletions: raw.videoCompletions,
      ctr: raw.ctr,
      cpm: raw.billedCpm ?? raw.clientFacingCpm ?? raw.cpm,
      cpc: raw.cpc,
      cpa: raw.cpa,
      datePreset: raw.datePreset,
      startDate: raw.startDate,
      endDate: raw.endDate,
      source: raw.snapshotSource || raw.toplineSource || raw.source,
    })
  );
}

export function getToplineSourceFreshness(topline) {
  const raw = topline?.raw || {};
  return (
    raw.sourceUpdatedAt ||
    raw.sourceLastUpdatedAt ||
    raw.latestIngestedAt ||
    raw.ingestUpdatedAt ||
    raw.snapshotUpdatedAt ||
    raw.updatedAt ||
    raw.generatedAt ||
    raw.dataFreshness ||
    ""
  );
}

export function readCachedAnalyticsIntelligence(
  storageKey,
  { scopeKey, metricSignature = "", sourceFreshness = "", maxAgeMs = DEFAULT_MAX_AGE_MS } = {}
) {
  const storage = getSessionStorage();
  if (!storage || !storageKey || !scopeKey) return null;
  try {
    const entry = JSON.parse(storage.getItem(storageKey) || "null");
    if (!entry || entry.version !== CACHE_VERSION || entry.scopeKey !== scopeKey) return null;
    if (maxAgeMs > 0 && Date.now() - Number(entry.cachedAt || 0) > maxAgeMs) return null;
    if (metricSignature && entry.metricSignature !== metricSignature) return null;
    if (sourceFreshness && entry.sourceFreshness && entry.sourceFreshness !== sourceFreshness) {
      return null;
    }
    return entry;
  } catch {
    return null;
  }
}

export function writeCachedAnalyticsIntelligence(
  storageKey,
  { scopeKey, metricSignature = "", sourceFreshness = "", payload }
) {
  const storage = getSessionStorage();
  if (!storage || !storageKey || !scopeKey) return;
  try {
    storage.setItem(
      storageKey,
      JSON.stringify({
        version: CACHE_VERSION,
        cachedAt: Date.now(),
        scopeKey,
        metricSignature,
        sourceFreshness,
        payload,
      })
    );
  } catch {
    // Session storage can be unavailable or full; analytics still works without restore.
  }
}
